import { useMemo } from "react";
import { doc, collection, query, where } from "firebase/firestore";
import type { User } from "firebase/auth";

import { useAuth } from "@/firebase/useAuth";
import { db } from "@/firebase/firebase-config";
import { useFirestoreQuery } from "./useFirestoreQuery";
import { useWeeklyProgress } from "./useWeeklyProgress";import { useMyLessons } from "./useMyLessons";
export type UserProgress = {
  onboardingCompletedAt?: string | null;
  onboardingStep?: string | null;
  lessonsCompleted?: number;
  weeklyLessonsCompleted?: number;
  weeklyGoalReached?: boolean;
  weekStartDate?: string | null;
  streakDays?: number;
  badges?: string[];
};

type ProgressState = {
  progress: (UserProgress & { id?: string }) | null;
  loading: boolean;
  error: string | null;
};

export function useProgress(explicitUser?: User | null): ProgressState {
  const { user, loading: authLoading } = useAuth();
  const activeUser = explicitUser ?? user;

  const progressDoc = useMemo(
    () => (activeUser ? doc(db(), "users", activeUser.uid, "meta", "progress") : undefined),
    [activeUser]
  );

  const { data: progressData, loading: progressLoading, error: progressError } = useFirestoreQuery<UserProgress>(progressDoc);

  // Calculate weekly progress from myLessons collection (accurate real-time data)
  const weeklyProgressData = useWeeklyProgress();

  // Get all lessons from myLessons collection to count completed ones
  const { myLessons, loading: myLessonsLoading } = useMyLessons();

  // Calculate lessons completed from myLessons collection (accurate count)
  const lessonsCompleted = useMemo(() => {
    let count = 0;
    myLessons.forEach((lesson) => {
      if (lesson.status === "completed") {
        count++;
      }
    });
    return count;
  }, [myLessons]);

  // Combine progress data with calculated lessons count and accurate weekly progress
  const progress = useMemo(() => {
    if (!progressData && lessonsCompleted === 0) return null;
    return {
      ...(progressData || {}),
      lessonsCompleted,
      // Override meta/progress weekly data with accurate calculated values
      weeklyLessonsCompleted: weeklyProgressData.weeklyLessonsCompleted,
      weeklyGoalReached: weeklyProgressData.weeklyGoalReached,
      weekStartDate: weeklyProgressData.weekStartDate,
    } as UserProgress & { id?: string };
  }, [progressData, lessonsCompleted, weeklyProgressData]);

  return {
    progress,
    loading: authLoading || progressLoading || myLessonsLoading || weeklyProgressData.loading,
    error: progressError,
  };
}
